import React, { useState, useEffect } from 'react';
import { useLoginContext } from '../context/LoginContext';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import {
  Shield,
  Activity,
  Lock,
  Bell,
  Globe,
  Zap,
  Key,
  Plus,
  Database,
  Network,
  Shield as ShieldIcon,
  Eye,
  EyeOff,
  X,
  Check,
  AlertTriangle,
  Settings as SettingsIcon,
  CreditCard,
  Users,
  Smartphone,
  MapPin,
  Calendar,
  Crown,
  CheckCircle,
  XCircle,
  Copy,
  RefreshCw
} from 'lucide-react';
import {
  fetchOrganizationDetails,
  fetchLoginHistory,
} from '../utils/apiWrapper';
import { apiRequest } from '../utils/api';

/* ────────────────────────────── Styled Components ────────────────────────────── */

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding: 1rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.375rem 0;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: -0.025em;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const TabsNav = styled.div`
  display: flex;
  gap: 0.375rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
    gap: 0.25rem;
  }
`;

const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.625rem 0.875rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ active }) => (active ? '#3b82f6' : '#64748b')};
  border-radius: 0.5rem;
  position: relative;
  white-space: nowrap;

  &:hover {
    background: ${({ active }) => (active ? '#f0f7ff' : '#f8fafc')};
    color: ${({ active }) => (active ? '#3b82f6' : '#1e293b')};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0.5rem;
    right: 0.5rem;
    height: 2px;
    background: ${({ active }) => (active ? '#3b82f6' : 'transparent')};
    border-radius: 1px;
    transition: background 0.2s ease;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.5rem 0.75rem;
    gap: 0.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.6875rem;
    padding: 0.4375rem 0.625rem;
  }
`;

const TabContent = styled.div`
  animation: fadeIn 0.3s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1.25rem 0;
  color: #1e293b;
  letter-spacing: -0.025em;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  border: 1px solid #f1f5f9;
  margin-bottom: 1.25rem;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    border-radius: 0.625rem;
  }
`;

const CardHeader = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const PlanInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
`;

const PlanTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const PlanPrice = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 0.8125rem;
  box-shadow: 0 1px 2px rgba(59, 130, 246, 0.3);
  line-height: 1.2;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.875rem;
    font-size: 0.75rem;
  }
`;

const SuccessButton = styled(PrimaryButton)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 1px 2px rgba(16, 185, 129, 0.3);

  &:hover {
    box-shadow: 0 3px 8px rgba(16, 185, 129, 0.4);
  }
`;

const DisabledButton = styled(PrimaryButton)`
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  box-shadow: 0 1px 2px rgba(107, 114, 128, 0.3);
  cursor: not-allowed;

  &:hover {
    transform: none;
    box-shadow: 0 1px 2px rgba(107, 114, 128, 0.3);
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 1.25rem;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const UsageCard = styled(Card)`
  margin-bottom: 1.25rem;
`;

const UsageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
    margin-bottom: 0.875rem;
  }
`;

const IconWrapper = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  background: ${props => props.color || '#3b82f6'}15;
  color: ${props => props.color || '#3b82f6'};
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
  
  @media (max-width: 768px) {
    width: 2.25rem;
    height: 2.25rem;
    
    svg {
      width: 1.125rem;
      height: 1.125rem;
    }
  }
`;

const UsageInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UsageTitle = styled.h4`
  font-size: 0.9375rem;
  font-weight: 600;
  margin: 0 0 0.125rem 0;
  color: #1e293b;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const UsageStats = styled.p`
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const ProgressContainer = styled.div`
  height: 0.375rem;
  background: #f1f5f9;
  border-radius: 9999px;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${props => props.color || '#3b82f6'} 0%, ${props => props.color || '#1d4ed8'} 100%);
  border-radius: inherit;
  transition: width 0.5s ease;
`;

const FeaturesGrid = styled.div`
  display: grid;
  gap: 0.875rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  padding: 1rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    gap: 0.625rem;
  }
`;

const FeatureText = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const ActivityCard = styled(Card)`
  margin-bottom: 0.75rem;
`;

const ActivityContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem;
  gap: 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const ActivityInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    gap: 0.75rem;
  }
`;

const StatusIndicator = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${props => props.success ? '#10b981' : '#ef4444'};
  box-shadow: 0 0 0 2px ${props => props.success ? '#10b98120' : '#ef444420'};
  flex-shrink: 0;
`;

const ActivityDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
`;

const ActivityTitle = styled.p`
  margin: 0;
  font-weight: 500;
  font-size: 0.8125rem;
  color: #1e293b;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const ActivitySubtitle = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  line-height: 1.3;
  
  svg {
    width: 0.75rem;
    height: 0.75rem;
    min-width: 0.75rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const ActivityMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-align: right;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    text-align: left;
  }
`;

const ActivityDate = styled.span`
  color: #64748b;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  white-space: nowrap;
  
  svg {
    width: 0.75rem;
    height: 0.75rem;
    min-width: 0.75rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const ActivityStatus = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${props => props.success ? '#10b981' : '#ef4444'};
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const SecurityCard = styled(Card)`
  margin-bottom: 1rem;
`;

const SecurityContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem;
  gap: 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const SecurityInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    gap: 0.75rem;
  }
`;

const SecurityDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
`;

const SecurityTitle = styled.p`
  margin: 0;
  font-weight: 500;
  font-size: 0.8125rem;
  color: #1e293b;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const SecurityDescription = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.75rem;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const NotificationCard = styled(Card)`
  margin-bottom: 0.75rem;
`;

const NotificationContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem;
  gap: 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const ToggleSwitch = styled.button`
  width: 2.75rem;
  height: 1.5rem;
  border-radius: 9999px;
  background: ${({ enabled }) =>
    enabled
      ? 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)'
      : '#f1f5f9'};
  border: none;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ enabled }) =>
    enabled
      ? '0 1px 3px rgba(59, 130, 246, 0.3)'
      : '0 1px 2px rgba(0, 0, 0, 0.1)'};
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
  }
`;

const ToggleCircle = styled.div`
  position: absolute;
  top: 0.1875rem;
  left: 0.1875rem;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 50%;
  background: white;
  transition: transform 0.2s ease;
  transform: ${({ enabled }) =>
    enabled ? 'translateX(1.25rem)' : 'translateX(0)'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

/* ─────────── New Domain Verification Components ─────────── */

const DomainList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DomainItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: ${props => props.isMain ? '#f0f7ff' : '#f8fafc'};
  border: 1px solid ${props => props.isMain ? '#bfdbfe' : '#f1f5f9'};
  border-radius: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const DomainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
`;

const DomainStatus = styled.span`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  background: ${props => props.verified ? '#dcfce7' : '#fef2f2'};
  color: ${props => props.verified ? '#166534' : '#991b1b'};
  
  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

const VerificationCard = styled(Card)`
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const VerificationStep = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

const InstructionsBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  margin: 1rem 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.75rem;
  line-height: 1.5;
  color: #334155;
  white-space: pre-wrap;
  word-break: break-all;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
    padding: 0.75rem;
  }
`;

const DomainInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DomainInput = styled.input`
  flex: 1;
  padding: 0.625rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  background: #f9fafb;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.5rem;
  }
`;

const TokenDisplay = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.875rem;
  margin: 1rem 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.75rem;
  word-break: break-all;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const TokenValue = styled.code`
  color: #3b82f6;
  background: #eff6ff;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const LoadingSpinner = styled.div`
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  width: 1rem;
  height: 1rem;
  border: 2px solid #f1f5f9;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
`;

const DomainTypeBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  background: ${props => props.type === 'main' ? '#dbeafe' : props.type === 'verified' ? '#dcfce7' : '#fef3c7'};
  color: ${props => props.type === 'main' ? '#1e40af' : props.type === 'verified' ? '#166534' : '#92400e'};
  margin-left: 0.5rem;
`;

/* ─────────── Modal Components ─────────── */

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-width: 400px;
  width: calc(100% - 1.5rem);
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
  border: 1px solid #f1f5f9;

  @keyframes slideUp {
    from { 
      transform: translateY(20px) scale(0.95); 
      opacity: 0; 
    }
    to { 
      transform: translateY(0) scale(1); 
      opacity: 1; 
    }
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    width: calc(100% - 1rem);
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.875rem;
  border-bottom: 1px solid #f1f5f9;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: #1e293b;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.375rem;
  border-radius: 0.5rem;
  color: #64748b;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const FormField = styled.div`
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    margin-bottom: 0.875rem;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.625rem;
  padding-right: 2.75rem;
  border-radius: 0.5rem;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #1e293b;
  font-size: 0.8125rem;
  transition: all 0.2s ease;
  box-sizing: border-box;
  line-height: 1.2;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f3f4f6;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    padding-right: 2.5rem;
    font-size: 0.75rem;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.625rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #64748b;
  transition: color 0.2s ease;

  &:hover {
    color: #1e293b;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #f1f5f9;
  
  @media (max-width: 768px) {
    margin-top: 1.25rem;
    padding-top: 1rem;
  }
`;

const SecondaryButton = styled.button`
  background: #f8fafc;
  color: #64748b;
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #f1f5f9;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.8125rem;
  transition: all 0.2s ease;
  line-height: 1.2;

  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.875rem;
    font-size: 0.75rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1.5rem;
  color: #64748b;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const EmptyStateIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  margin: 0 auto 0.875rem;
  background: #f8fafc;
  color: #94a3b8;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
  
  @media (max-width: 768px) {
    width: 2.25rem;
    height: 2.25rem;
    margin-bottom: 0.75rem;
    
    svg {
      width: 1.125rem;
      height: 1.125rem;
    }
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.375rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const EmptyStateText = styled.p`
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

/* ────────────────────────────── Component ────────────────────────────── */

function SettingsPage() {
  const { user } = useLoginContext();
  const userTimeZone = user?.timeZone || moment.tz.guess();

  const [activeTab, setActiveTab] = useState('subscription');
  const [organization, setOrganization] = useState(null);
  const [loginActivity, setLoginActivity] = useState([]);
  const [notifications, setNotifications] = useState({
    scanCompletion: true,
    criticalVulnerabilities: true,
    teamUpdates: false,
  });

  // Password modal state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Domain verification state
  const [verifiedDomains, setVerifiedDomains] = useState([]);
  const [crossDomainList, setCrossDomainList] = useState([]);
  const [organizationDomain, setOrganizationDomain] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [verificationStep, setVerificationStep] = useState('input');
  const [verificationToken, setVerificationToken] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [currentDomain, setCurrentDomain] = useState('');
  const [loadingDomains, setLoadingDomains] = useState(false);

  const navigate = useNavigate();

  // Tabs configuration
  const tabs = [
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'domains', label: 'Domains', icon: Globe },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  // Load data
  useEffect(() => {
    async function loadData() {
      try {
        const orgDetails = await fetchOrganizationDetails();
        setOrganization(orgDetails);

        // Load domains data after organization is loaded
        if (orgDetails) {
          await loadDomainsData();
        }
      } catch (error) {
        console.error('Error fetching organization details:', error);
        toast.error('Failed to load organization details');
      }

      try {
        const loginHistoryResponse = await fetchLoginHistory();
        setLoginActivity(loginHistoryResponse.loginHistory || []);
      } catch (error) {
        console.error('Error fetching login history:', error);
        toast.error('Failed to load login history');
      }
    }
    loadData();
  }, []);

  // Load domains data
  const loadDomainsData = async () => {
    try {
      setLoadingDomains(true);
      const domainsResponse = await apiRequest('/api/domain/verified', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (domainsResponse.ok) {
        const domainsData = await domainsResponse.json();
        setVerifiedDomains(domainsData.verifiedDomains || []);
        setCrossDomainList(domainsData.crossDomainList || []);
        setOrganizationDomain(domainsData.organizationDomain || '');
      }
    } catch (error) {
      console.error('Error fetching domain data:', error);
      toast.error('Failed to load domain verification data');
    } finally {
      setLoadingDomains(false);
    }
  };

  // Get icon for scan type
  const getIconForScanType = (type) => {
    switch (type.toLowerCase()) {
      case 'website': return Globe;
      case 'api': return Database;
      case 'network': return Network;
      case 'firewall': return ShieldIcon;
      default: return Globe;
    }
  };

  // Get color for scan type
  const getColorForScanType = (type) => {
    switch (type.toLowerCase()) {
      case 'website': return '#3b82f6';
      case 'api': return '#10b981';
      case 'network': return '#f59e0b';
      case 'firewall': return '#ef4444';
      default: return '#3b82f6';
    }
  };

  // Dynamic scan usage data
  const dynamicScanUsage = organization?.subscription?.scanTypes
    ? Object.keys(organization.subscription.scanTypes).map((key) => ({
      type: key.charAt(0).toUpperCase() + key.slice(1) + ' Scan',
      used: organization.subscription.scansUsed?.[key] || 0,
      total: organization.subscription.scanTypes[key],
      icon: getIconForScanType(key),
      color: getColorForScanType(key),
    }))
    : [];

  // Map login activity
  const mappedLoginActivity = loginActivity.map((activity, index) => ({
    id: index,
    device: activity.deviceInfo,
    location: activity.ipAddress,
    date: moment(activity.loginDate).tz(userTimeZone).format('MMM D, YYYY, h:mm A'),
    status: activity.success ? 'Success' : 'Failed',
    success: activity.success,
  }));

  // Handlers for password change
  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long.');
      return;
    }

    try {
      const response = await apiRequest('/api/auth/settings/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldPassword, newPassword }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Password updated successfully!');
        setIsPasswordModalOpen(false);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(result.message || 'Error updating password.');
      }
    } catch (error) {
      toast.error('Error updating password.');
    }
  };

  const handleNotificationToggle = async (notificationType) => {
    try {
      const newValue = !notifications[notificationType];
      const response = await apiRequest('/api/auth/settings/notifications/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: notificationType, enabled: newValue }),
      });

      if (response.ok) {
        setNotifications(prev => ({ ...prev, [notificationType]: newValue }));
        toast.success('Notification settings updated successfully!');
      } else {
        toast.error('Failed to update notification settings');
      }
    } catch (error) {
      toast.error('Error updating notification settings');
    }
  };

  // Domain verification handlers
  const handleInitiateVerification = async () => {
    if (!domainInput.trim()) {
      toast.error('Please enter a domain');
      return;
    }

    // Basic domain validation
    const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    if (!domainRegex.test(domainInput)) {
      toast.error('Please enter a valid domain (e.g., example.com)');
      return;
    }

    try {
      setIsVerifying(true);
      const response = await apiRequest('/api/domain/verify/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: domainInput.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setCurrentDomain(domainInput.trim());
        setVerificationToken(data.verificationToken);
        setVerificationStep('token');
        toast.success('Verification initiated successfully');
      } else {
        toast.error(data.message || 'Failed to initiate verification');
      }
    } catch (error) {
      toast.error('Error initiating domain verification');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!verificationToken) {
      toast.error('No verification token found');
      return;
    }

    try {
      setIsVerifying(true);
      const response = await apiRequest('/api/domain/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain: currentDomain,
          verificationToken: verificationToken,
        }),
      });

      const data = await response.json();

      if (response.ok && data.verified) {
        toast.success('Domain verified successfully!');

        // Refresh domain list
        await loadDomainsData();

        // Reset form
        setVerificationStep('input');
        setDomainInput('');
        setVerificationToken('');
        setCurrentDomain('');
      } else {
        toast.error(data.message || 'Verification failed');
      }
    } catch (error) {
      toast.error('Error verifying domain');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(`vulnerability-scanner-verification=${verificationToken}`);
    toast.success('TXT record value copied to clipboard');
  };

  const handleCancelVerification = () => {
    setVerificationStep('input');
    setDomainInput('');
    setVerificationToken('');
    setCurrentDomain('');
    setIsVerifying(false);
  };

  const planName = organization?.subscription?.planName || 'Professional';
  const planPrice = organization?.subscription?.price ? `$${organization.subscription.price}/scan` : '₹ 999/scan';

  return (
    <PageContainer>
      {/* Tabs Navigation */}
      <TabsNav>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <TabButton
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={16} />
              {tab.label}
            </TabButton>
          );
        })}
      </TabsNav>

      <TabContent>
        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div>
            <Card>
              <CardHeader>
                <div>
                  <PlanInfo>
                    <PlanTitle>
                      <Crown size={18} color="#f59e0b" />
                      {planName}
                    </PlanTitle>
                  </PlanInfo>
                  <PlanPrice>{planPrice}</PlanPrice>
                </div>
                <PrimaryButton onClick={() => navigate('/upgrade-plan')}>
                  <Plus size={14} />
                  Upgrade Plan
                </PrimaryButton>
              </CardHeader>
            </Card>

            <SectionTitle>Usage Overview</SectionTitle>
            <Grid>
              {dynamicScanUsage.map((scan) => {
                const Icon = scan.icon;
                const percentage = (scan.used / scan.total) * 100;
                return (
                  <UsageCard key={scan.type}>
                    <CardContent>
                      <UsageHeader>
                        <IconWrapper color={scan.color}>
                          <Icon size={20} />
                        </IconWrapper>
                        <UsageInfo>
                          <UsageTitle>{scan.type}</UsageTitle>
                          <UsageStats>
                            {scan.used} of {scan.total} used
                          </UsageStats>
                        </UsageInfo>
                      </UsageHeader>
                      <ProgressContainer>
                        <ProgressBar
                          style={{ width: `${percentage}%` }}
                          color={scan.color}
                        />
                      </ProgressContainer>
                    </CardContent>
                  </UsageCard>
                );
              })}
            </Grid>

            <SectionTitle style={{ marginTop: '2rem' }}>Plan Features</SectionTitle>
            <FeaturesGrid>
              <FeatureCard>
                <IconWrapper color="#10b981">
                  <Users size={16} />
                </IconWrapper>
                <FeatureText>Unlimited Team Members</FeatureText>
              </FeatureCard>
              <FeatureCard>
                <IconWrapper color="#f59e0b">
                  <Zap size={16} />
                </IconWrapper>
                <FeatureText>Priority Support</FeatureText>
              </FeatureCard>
              <FeatureCard>
                <IconWrapper color="#3b82f6">
                  <Shield size={16} />
                </IconWrapper>
                <FeatureText>Automated Security Scans</FeatureText>
              </FeatureCard>
            </FeaturesGrid>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div>
            <SectionTitle>Recent Login Activity</SectionTitle>
            {mappedLoginActivity.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>
                  <Activity size={20} />
                </EmptyStateIcon>
                <EmptyStateTitle>No recent activity</EmptyStateTitle>
                <EmptyStateText>Your login activity will appear here.</EmptyStateText>
              </EmptyState>
            ) : (
              mappedLoginActivity.map((activity) => (
                <ActivityCard key={activity.id}>
                  <ActivityContent>
                    <ActivityInfo>
                      <StatusIndicator success={activity.success} />
                      <ActivityDetails>
                        <ActivityTitle>
                          <Smartphone size={12} style={{ marginRight: '0.375rem' }} />
                          {activity.device}
                        </ActivityTitle>
                        <ActivitySubtitle>
                          <MapPin size={11} />
                          {activity.location}
                        </ActivitySubtitle>
                      </ActivityDetails>
                    </ActivityInfo>
                    <ActivityMeta>
                      <ActivityDate>
                        <Calendar size={11} style={{ marginRight: '0.375rem' }} />
                        {activity.date}
                      </ActivityDate>
                      <ActivityStatus success={activity.success}>
                        {activity.status}
                      </ActivityStatus>
                    </ActivityMeta>
                  </ActivityContent>
                </ActivityCard>
              ))
            )}
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div>
            <SectionTitle>Security Settings</SectionTitle>

            <SecurityCard>
              <SecurityContent>
                <SecurityInfo>
                  <IconWrapper color="#10b981">
                    <Shield size={18} />
                  </IconWrapper>
                  <SecurityDetails>
                    <SecurityTitle>Two-Factor Authentication</SecurityTitle>
                    <SecurityDescription>
                      Add an extra layer of security to your account
                    </SecurityDescription>
                  </SecurityDetails>
                </SecurityInfo>
                <SuccessButton>
                  <Check size={14} />
                  Enabled
                </SuccessButton>
              </SecurityContent>
            </SecurityCard>

            <SecurityCard>
              <SecurityContent>
                <SecurityInfo>
                  <IconWrapper color="#3b82f6">
                    <Key size={18} />
                  </IconWrapper>
                  <SecurityDetails>
                    <SecurityTitle>Password</SecurityTitle>
                    <SecurityDescription>
                      Update your password regularly for better security
                    </SecurityDescription>
                  </SecurityDetails>
                </SecurityInfo>
                <PrimaryButton onClick={() => setIsPasswordModalOpen(true)}>
                  <Key size={14} />
                  Change Password
                </PrimaryButton>
              </SecurityContent>
            </SecurityCard>

            <SecurityCard>
              <SecurityContent>
                <SecurityInfo>
                  <IconWrapper color="#f59e0b">
                    <Globe size={18} />
                  </IconWrapper>
                  <SecurityDetails>
                    <SecurityTitle>API Keys</SecurityTitle>
                    <SecurityDescription>
                      Manage your API keys and integrations
                    </SecurityDescription>
                  </SecurityDetails>
                </SecurityInfo>
                <DisabledButton>
                  <AlertTriangle size={14} />
                  Coming Soon
                </DisabledButton>
              </SecurityContent>
            </SecurityCard>
          </div>
        )}

        {/* Domains Tab */}
        {activeTab === 'domains' && (
          <div>
            <SectionTitle>Domain Verification</SectionTitle>

            <VerificationCard>
              <CardHeader>
                <div>
                  <PlanTitle>Add Domain for Cross-Domain Scanning</PlanTitle>
                  <PlanPrice>Verify ownership to scan domains outside your organization</PlanPrice>
                </div>
              </CardHeader>

              <CardContent>
                {/* Step 1: Domain Input */}
                <VerificationStep active={verificationStep === 'input'}>
                  <p style={{ marginBottom: '1rem', color: '#64748b', fontSize: '0.875rem' }}>
                    Enter a domain you want to scan. You'll need to verify ownership by adding a TXT record.
                  </p>

                  <DomainInputContainer>
                    <DomainInput
                      type="text"
                      placeholder="Enter domain (e.g., example.com)"
                      value={domainInput}
                      onChange={(e) => setDomainInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleInitiateVerification()}
                    />
                    <PrimaryButton
                      onClick={handleInitiateVerification}
                      disabled={isVerifying || !domainInput.trim()}
                    >
                      {isVerifying ? (
                        <>
                          <LoadingSpinner />
                          Initiating...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={14} />
                          Verify Domain
                        </>
                      )}
                    </PrimaryButton>
                  </DomainInputContainer>
                </VerificationStep>

                {/* Step 2: Token Display */}
                <VerificationStep active={verificationStep === 'token'}>
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 600 }}>
                      Verify Domain: {currentDomain}
                    </h4>
                    <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                      Add this TXT record to your domain's DNS settings:
                    </p>

                    <InstructionsBox>
                      TXT Record Name: @ or {currentDomain}
                      TXT Record Value: vulnerability-scanner-verification={verificationToken}
                      TTL: 3600 (or default)

                      Note: DNS changes may take up to 24 hours to propagate.
                    </InstructionsBox>

                    <TokenDisplay>
                      <TokenValue>vulnerability-scanner-verification={verificationToken}</TokenValue>
                      <CopyButton onClick={handleCopyToken}>
                        <Copy size={12} />
                        Copy TXT Value
                      </CopyButton>
                    </TokenDisplay>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <SecondaryButton onClick={handleCancelVerification}>
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton
                      onClick={handleVerifyDomain}
                      disabled={isVerifying}
                    >
                      {isVerifying ? (
                        <>
                          <LoadingSpinner />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={14} />
                          I've Added the TXT Record
                        </>
                      )}
                    </PrimaryButton>
                    <SecondaryButton
                      onClick={handleInitiateVerification}
                      disabled={isVerifying}
                    >
                      <RefreshCw size={12} />
                      Regenerate Token
                    </SecondaryButton>
                  </div>
                </VerificationStep>
              </CardContent>
            </VerificationCard>

            {/* Domain Lists */}
            <SectionTitle>Your Domains</SectionTitle>

            {/* Loading State */}
            {loadingDomains ? (
              <EmptyState>
                <LoadingSpinner style={{ margin: '0 auto 1rem', width: '2rem', height: '2rem' }} />
                <EmptyStateTitle>Loading domains...</EmptyStateTitle>
                <EmptyStateText>Please wait while we fetch your domain information</EmptyStateText>
              </EmptyState>
            ) : (
              <>
                {/* Main Organization Domain */}
                {organizationDomain && (
                  <DomainList style={{ marginBottom: '1.5rem' }}>
                    <DomainItem isMain>
                      <DomainInfo>
                        <IconWrapper color="#3b82f6">
                          <Globe size={16} />
                        </IconWrapper>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                            <span style={{ fontWeight: 500, fontSize: '0.875rem', color: '#1e293b' }}>
                              {organizationDomain}
                            </span>
                            <DomainTypeBadge type="main">Main Domain</DomainTypeBadge>
                          </div>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            Automatically verified as your organization's primary domain
                          </span>
                        </div>
                        <DomainStatus verified>
                          <CheckCircle size={12} />
                          Verified
                        </DomainStatus>
                      </DomainInfo>
                    </DomainItem>
                  </DomainList>
                )}

                {/* Verified Domains */}
                {verifiedDomains.filter(domain => domain !== organizationDomain).length > 0 && (
                  <>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.75rem 0', color: '#1e293b' }}>
                      Verified Domains ({verifiedDomains.filter(domain => domain !== organizationDomain).length})
                    </h3>
                    <DomainList style={{ marginBottom: '1.5rem' }}>
                      {verifiedDomains
                        .filter(domain => domain !== organizationDomain)
                        .map((domain, index) => (
                          <DomainItem key={index}>
                            <DomainInfo>
                              <IconWrapper color="#10b981">
                                <CheckCircle size={16} />
                              </IconWrapper>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                                  <span style={{ fontWeight: 500, fontSize: '0.875rem', color: '#1e293b' }}>
                                    {domain}
                                  </span>
                                  <DomainTypeBadge type="verified">Verified</DomainTypeBadge>
                                </div>
                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                  Verified via TXT record
                                </span>
                              </div>
                              <DomainStatus verified>
                                <CheckCircle size={12} />
                                Verified
                              </DomainStatus>
                            </DomainInfo>
                          </DomainItem>
                        ))}
                    </DomainList>
                  </>
                )}

                {/* Cross-Domain List */}
                {crossDomainList.length > 0 && (
                  <>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.75rem 0', color: '#1e293b' }}>
                      Cross-Domain Allow List ({crossDomainList.length})
                    </h3>
                    <DomainList>
                      {crossDomainList.map((domain, index) => (
                        <DomainItem key={index}>
                          <DomainInfo>
                            <IconWrapper color="#f59e0b">
                              <Globe size={16} />
                            </IconWrapper>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                                <span style={{ fontWeight: 500, fontSize: '0.875rem', color: '#1e293b' }}>
                                  {domain}
                                </span>
                                <DomainTypeBadge type="allowed">Allowed</DomainTypeBadge>
                              </div>
                              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                Manually added to cross-domain allow list
                              </span>
                            </div>
                            <DomainStatus verified={false}>
                              <XCircle size={12} />
                              Requires Verification
                            </DomainStatus>
                          </DomainInfo>
                        </DomainItem>
                      ))}
                    </DomainList>
                  </>
                )}

                {/* Empty States */}
                {verifiedDomains.length <= 1 && crossDomainList.length === 0 && (
                  <EmptyState>
                    <EmptyStateIcon>
                      <Globe size={20} />
                    </EmptyStateIcon>
                    <EmptyStateTitle>No additional domains</EmptyStateTitle>
                    <EmptyStateText>
                      Add domains above to enable cross-domain scanning capabilities.
                    </EmptyStateText>
                  </EmptyState>
                )}
              </>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div>
            <SectionTitle>Notification Preferences</SectionTitle>

            {[
              {
                id: 'scanCompletion',
                title: 'Scan Completion',
                description: 'Get notified when security scans are completed',
                enabled: notifications.scanCompletion,
                icon: Check,
                color: '#10b981'
              },
              {
                id: 'criticalVulnerabilities',
                title: 'Critical Vulnerabilities',
                description: 'Immediate alerts for critical security issues',
                enabled: notifications.criticalVulnerabilities,
                icon: AlertTriangle,
                color: '#ef4444'
              },
              {
                id: 'teamUpdates',
                title: 'Team Updates',
                description: 'Notifications about team member activities',
                enabled: notifications.teamUpdates,
                icon: Users,
                color: '#3b82f6'
              },
            ].map((notification) => {
              const Icon = notification.icon;
              return (
                <NotificationCard key={notification.id}>
                  <NotificationContent>
                    <SecurityInfo>
                      <IconWrapper color={notification.color}>
                        <Icon size={18} />
                      </IconWrapper>
                      <SecurityDetails>
                        <SecurityTitle>{notification.title}</SecurityTitle>
                        <SecurityDescription>{notification.description}</SecurityDescription>
                      </SecurityDetails>
                    </SecurityInfo>
                    <ToggleSwitch
                      enabled={notification.enabled}
                      onClick={() => handleNotificationToggle(notification.id)}
                    >
                      <ToggleCircle enabled={notification.enabled} />
                    </ToggleSwitch>
                  </NotificationContent>
                </NotificationCard>
              );
            })}
          </div>
        )}
      </TabContent>

      {/* Change Password Modal */}
      {isPasswordModalOpen && (
        <ModalOverlay onClick={() => setIsPasswordModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Change Password</ModalTitle>
              <CloseButton onClick={() => setIsPasswordModalOpen(false)}>
                <X size={16} />
              </CloseButton>
            </ModalHeader>

            <FormField>
              <Label>Current Password</Label>
              <PasswordInputWrapper>
                <Input
                  type={showOldPassword ? 'text' : 'password'}
                  placeholder="Enter current password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                >
                  {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </PasswordToggle>
              </PasswordInputWrapper>
            </FormField>

            <FormField>
              <Label>New Password</Label>
              <PasswordInputWrapper>
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </PasswordToggle>
              </PasswordInputWrapper>
            </FormField>

            <FormField>
              <Label>Confirm New Password</Label>
              <PasswordInputWrapper>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </PasswordToggle>
              </PasswordInputWrapper>
            </FormField>

            <ModalActions>
              <SecondaryButton onClick={() => setIsPasswordModalOpen(false)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={handlePasswordChange}>
                <Key size={14} />
                Update Password
              </PrimaryButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Toast Container */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#1e293b',
            fontSize: '0.8125rem',
            borderRadius: '0.5rem',
            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.15)',
            border: '1px solid #f1f5f9'
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </PageContainer>
  );
}

export default SettingsPage;